import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { useIsMobile } from 'WindowDimensionProvider';
import { SkeletonLineSlim } from 'components/theme/Skeleton/Skeleton';
import { PriceTierWithParticipants } from 'components/Booking/PriceTier/types';
import PriceTiersSummary from 'components/Resource/PriceTiersSummary';
import { getListingCoverUrl, getListingCoverAltText } from 'utils/listings';
import CancellationPolicySummary from 'features/ShoppingCart/components/CancellationPolicySummary';

interface Props {
  priceTiers: PriceTierWithParticipants[];
}

const TicketSummary: FC<Props> = ({ priceTiers }) => {
  const isMobile = useIsMobile();
  const { isLoading, listing, cancellationPolicy } = useEventBookingSession();

  const formatterPriceTiers = useMemo(() => {
    return priceTiers.map((tier) => {
      return {
        ...tier,
        selectedNumber: tier.participants,
      };
    });
  }, [priceTiers]);

  const coverImageUrl = useMemo(
    () => (listing ? getListingCoverUrl(listing, isMobile) : ''),
    [listing, isMobile],
  );

  const coverAltText = useMemo(
    () => (listing ? getListingCoverAltText(listing) : null),
    [listing],
  );

  const taxes = useMemo(() => {
    if (listing?.taxes?.length) return listing.taxes;
    return listing?.resourceGroups?.[0]?.experiences?.[0]?.taxes ?? [];
  }, [listing]);

  if (isLoading || !listing) {
    return (
      <Wrapper>
        <SkeletonLineSlim />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <PriceTiersSummary
        priceTiers={formatterPriceTiers}
        title={listing.title ?? 'Title not found'}
        taxes={taxes ?? []}
        coverImageUrl={coverImageUrl ?? ''}
        coverAltText={coverAltText}
        applyFlatFeeToEachTier={false}
      />
      <CancellationPolicySummary cancellationPolicy={cancellationPolicy} />
    </Wrapper>
  );
};

export default TicketSummary;

const Wrapper = styled.div`
  width: 350px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;
